import { Loader2 } from "lucide-react";
import { isValidElement, type ReactNode } from "react";
import { toast } from "sonner";

import {
  ToastBody,
  type ToastAction,
  type ToastType,
} from "@/components/ui/v2/sonner";

export type { ToastType, ToastAction };

export type ToastMessage = ReactNode;

export type ToastId = string | number;

export type ToastPosition =
  | "top-right"
  | "top-center"
  | "top-left"
  | "bottom-right"
  | "bottom-center"
  | "bottom-left";

export interface ToastOptions {
  duration?: number;
  position?: ToastPosition;
  id?: ToastId;
  description?: ReactNode;
  action?: ToastAction;
  dismissible?: boolean;
}

export interface ToastPayload {
  message: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  action?: ToastAction;
}

export type ToastContent = ReactNode | ToastPayload;

export type Resolvable<TValue, TArg> = TValue | ((arg: TArg) => TValue);

export interface PromiseToastMessages<T> {
  loading: ToastContent;
  success: Resolvable<ToastContent, T>;
  error: Resolvable<ToastContent, unknown>;
}

const DEFAULT_DURATION = 3000;
const DEFAULT_POSITION: ToastPosition = "top-right";

const unwrap = <TValue, TArg>(
  value: Resolvable<TValue, TArg>,
  arg: TArg,
): TValue =>
    typeof value === "function"
      ? (value as (arg: TArg) => TValue)(arg)
      : value;

const isToastPayload = (value: ToastContent): value is ToastPayload =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  !isValidElement(value) &&
  "message" in value;

const toPayload = (
  content: ToastContent,
  fallbackTitle?: ReactNode,
): ToastPayload =>
  isToastPayload(content)
    ? { ...content, title: content.title ?? fallbackTitle }
    : { message: content, title: fallbackTitle };

const nextToastId = (prefix: string): ToastId =>
  `${prefix}-${Date.now()}-${Math.random()}`;

interface RenderArgs {
  toastId: ToastId;
  type: ToastType;
  payload: ToastPayload;
  options: ToastOptions;
  spin?: boolean;
  duration?: number;
}

const renderToast = ({
  toastId,
  type,
  payload,
  options,
  spin,
  duration,
}: RenderArgs) => {
  const resolvedDuration = duration ?? options.duration ?? DEFAULT_DURATION;
  const position = options.position ?? DEFAULT_POSITION;
  const onDismiss = options.dismissible
    ? () => toast.dismiss(toastId)
    : undefined;

  toast.custom(
    () => (
      <ToastBody
        type={type}
        message={payload.message}
        title={payload.title}
        description={payload.description ?? options.description}
        action={payload.action ?? options.action}
        icon={spin ? Loader2 : undefined}
        spin={spin}
        onDismiss={onDismiss}
      />
    ),
    { id: toastId, position, duration: resolvedDuration },
  );
};

function showToast(
  message: ToastContent,
  type: ToastType = "success",
  options: ToastOptions = {},
  title?: ReactNode,
): ToastId {
  const toastId = options.id ?? nextToastId("toast");
  renderToast({
    toastId,
    type,
    payload: toPayload(message, title),
    options,
  });
  return toastId;
}

function showPromiseToast<T>(
  promise: Promise<T>,
  messages: PromiseToastMessages<T>,
  options: ToastOptions = {},
): ToastId {
  const toastId = options.id ?? nextToastId("promise");

  renderToast({
    toastId,
    type: "neutral",
    payload: toPayload(messages.loading),
    options,
    spin: true,
    duration: Infinity,
  });

  promise.then(
    (data) =>
      renderToast({
        toastId,
        type: "success",
        payload: toPayload(unwrap(messages.success, data)),
        options,
      }),
    (error: unknown) =>
      renderToast({
        toastId,
        type: "error",
        payload: toPayload(unwrap(messages.error, error)),
        options,
      }),
  );

  return toastId;
}

function dismissToast(toastId?: ToastId) {
  toast.dismiss(toastId);
}

function dismissAllToasts() {
  toast.dismiss();
}

const useToast = () => ({
  showToast,
  showPromiseToast,
  dismissToast,
  dismissAllToasts,
});

export default useToast;
